import React, {useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";

// Live "collected SEPA volume" counter, ported from the sepaheld.com hero.
// The displayed value is a pure function of the current time, so every visitor
// (and every reload) sees the same figure. The day-profile only redistributes
// the volume within a day; the monthly average stays exact.
//
// Update BASELINE_EUR/BASELINE_AT every few weeks at deploy time (set
// BASELINE_EUR to the value currently shown so the counter does not jump).
const BASELINE_EUR = 437_000_000; // real volume at the baseline date
const BASELINE_AT = Date.UTC(2026, 4, 26, 0, 0, 0); // 26 May 2026, 00:00 UTC
const RATE_EUR_PER_SEC = 2.31; // monthly average: monthly volume / 2_592_000

// Day profile (Europe/Berlin local time): highest during the day (peaks ~9 and
// ~14), very low at night. Values are relative weights, normalised so the
// monthly average is preserved.
const DAY_PROFILE = {
    base: 0.18,
    dayCenter: 13,
    dayWidth: 16,
    dayHeight: 0.8,
    peak1At: 9,
    peak1Height: 1.5,
    peak1Width: 1.3,
    peak2At: 14,
    peak2Height: 1.2,
    peak2Width: 1.5,
};

// Invoice jumps: the counter advances in realistic invoice amounts, not by 1 €.
const INVOICES = {
    minEuro: 39,
    maxEuro: 1080,
    skew: 4, // > 1 = many small, few large invoices
    seed: 20260526,
};

const wRaw = (h) => {
    const t = DAY_PROFILE;
    const dayBell = t.dayHeight * Math.max(0, Math.cos((Math.PI * (h - t.dayCenter)) / t.dayWidth));
    const peak1 = t.peak1Height * Math.exp(-Math.pow((h - t.peak1At) / t.peak1Width, 2) / 2);
    const peak2 = t.peak2Height * Math.exp(-Math.pow((h - t.peak2At) / t.peak2Width, 2) / 2);
    return t.base + dayBell + peak1 + peak2;
};

// Antiderivative table of (w_normalised - 1); g(0) = g(24) = 0.
const G_STEPS = 480;
const G_DH = 24 / G_STEPS;
const G_TABLE = (() => {
    const W = new Float64Array(G_STEPS + 1);
    for (let i = 1; i <= G_STEPS; i++) {
        const h0 = (i - 1) * G_DH;
        const h1 = i * G_DH;
        W[i] = W[i - 1] + ((wRaw(h0) + wRaw(h1)) / 2) * G_DH;
    }
    const mean = W[G_STEPS] / 24;
    const g = new Float64Array(G_STEPS + 1);
    for (let i = 0; i <= G_STEPS; i++) {
        g[i] = W[i] / mean - i * G_DH;
    }
    return g;
})();

const gAt = (phi) => {
    let x = phi / G_DH;
    if (x < 0) x = 0;
    if (x > G_STEPS) x = G_STEPS;
    const i = Math.min(G_STEPS - 1, Math.floor(x));
    const frac = x - i;
    return G_TABLE[i] * (1 - frac) + G_TABLE[i + 1] * frac;
};

const berlinFmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
});
const berlinHour = (ms) => {
    let h = 0;
    let m = 0;
    let s = 0;
    for (const p of berlinFmt.formatToParts(new Date(ms))) {
        if (p.type === "hour") h = +p.value;
        else if (p.type === "minute") m = +p.value;
        else if (p.type === "second") s = +p.value;
    }
    return h + m / 60 + s / 3600;
};

const G_BASELINE = gAt(berlinHour(BASELINE_AT));
const volumeSince = (nowMs) => {
    const elapsedSec = Math.max(0, (nowMs - BASELINE_AT) / 1000);
    const linear = RATE_EUR_PER_SEC * elapsedSec;
    const diurnal = RATE_EUR_PER_SEC * (gAt(berlinHour(nowMs)) - G_BASELINE) * 3600;
    return Math.max(0, linear + diurnal);
};

// Deterministic PRNG (mulberry32-style hash).
const rng = (input) => {
    let t = (input + 0x6d2b79f5) | 0;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const invoiceAmount = (n) => {
    const r = rng(INVOICES.seed + n);
    const span = INVOICES.maxEuro - INVOICES.minEuro;
    return INVOICES.minEuro + span * Math.pow(r, INVOICES.skew);
};

const VolumeCounter = () => {
    const intl = useIntl();
    const [amount, setAmount] = useState(BASELINE_EUR);
    const [justJumped, setJustJumped] = useState(false);

    const incrementRef = useRef(0);
    const counterRef = useRef(0);
    const prevRef = useRef(BASELINE_EUR);
    const flashTimer = useRef(null);

    useEffect(() => {
        const tick = () => {
            const target = volumeSince(Date.now());

            let next = invoiceAmount(counterRef.current);
            while (incrementRef.current + next <= target) {
                incrementRef.current += next;
                counterRef.current += 1;
                next = invoiceAmount(counterRef.current);
            }

            const value = BASELINE_EUR + incrementRef.current;

            if (value > prevRef.current) {
                prevRef.current = value;
                setJustJumped(true);
                if (flashTimer.current) clearTimeout(flashTimer.current);
                flashTimer.current = setTimeout(() => setJustJumped(false), 700);
            }
            setAmount(value);
        };

        tick();
        const id = setInterval(tick, 100);
        return () => {
            clearInterval(id);
            if (flashTimer.current) clearTimeout(flashTimer.current);
        };
    }, []);

    const formatted = intl.formatNumber(amount, {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return (
        <div className="flex w-full max-w-md items-start gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm shadow-sm sm:w-auto sm:items-center sm:rounded-full sm:py-2">
            <span className="relative mt-1 flex h-2 w-2 flex-none sm:mt-0" aria-hidden>
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/60 motion-safe:animate-ping"/>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"/>
            </span>
            <span className="text-slate-500">
                <FormattedMessage id="sepaheld.hero.volume.label" defaultMessage="SEPA volume collected via SepaHeld:"/>
                {" "}
                <span
                    className={
                        "font-bold tabular-nums transition-colors duration-300 " +
                        (justJumped ? "text-emerald-600" : "text-slate-700")
                    }
                >
                    {formatted}
                </span>
            </span>
        </div>
    );
};

export default VolumeCounter;
